import React, { useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { useTenant } from '../contexts/TenantContext';
import axios from '../config/axios.config';
import Swal from 'sweetalert2';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    const toast = useRef(null);
    const { tenantIdentifier } = useTenant();
    const [searchParams] = useSearchParams();
    const companyToken = searchParams.get('company');
    const advisorToken = searchParams.get('advisor'); // MLM Phase 2: Preserve advisor token

    // Helper: Build URL with preserved tokens
    const buildAuthUrl = (path) => {
        const params = [];
        if (companyToken) params.push(`company=${companyToken}`);
        if (advisorToken) params.push(`advisor=${advisorToken}`);
        return params.length > 0 ? `${path}?${params.join('&')}` : path;
    };

    const handleSubmit = async () => {
        setError('');

        if (!email) {
            setError('Email is required');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post('/tenant/forgot-password', {
                email,
                tenant: tenantIdentifier
            });

            setSuccess(true);
            
            // Show SweetAlert success message
            Swal.fire({
                title: 'Email Sent Successfully!',
                text: 'Password reset instructions sent to your email',
                icon: 'success',
                timer: 2000,
                timerProgressBar: true,
                showConfirmButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
            });

            // Close SweetAlert and navigate after short delay
            setTimeout(() => {
                Swal.close();
                navigate(`/app/login${companyToken ? `?company=${companyToken}` : ''}`);
            }, 1800);
        } catch (err) {
            const message = err.response?.data?.message || 'Failed to send reset email. Please try again.';
            setError(message);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: message,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(`/app/login${companyToken ? `?company=${companyToken}` : ''}`);
    };

    return (
        <>
            <Toast ref={toast} />
            <div className="flex min-h-screen">
                {/* Left Panel - Form Section */}
                <div className="w-full lg:w-4 min-h-screen lg:h-screen text-center px-4 py-4 lg:px-6 lg:py-6 flex flex-column justify-content-center lg:justify-content-between surface-ground overflow-y-auto">
                    {/* Logo - Hidden on mobile, shown on desktop */}
                    <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="mx-auto hidden lg:block" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                    <div className="flex flex-column align-items-center gap-3 lg:gap-4">
                        {/* Logo - Shown on mobile only */}
                        <img src={`${process.env.PUBLIC_URL}/layout/images/interestng_logo_updated.jpeg`} alt="INTERESTNG" className="block lg:hidden mb-3" style={{ height: '3rem', width: 'auto', mixBlendMode: 'multiply', filter: 'contrast(1.1) brightness(1.05)' }} />

                        <div className="mb-2 lg:mb-3">
                            <h2 className="text-xl lg:text-2xl">Reset Password</h2>
                            <p>
                                Enter your email address and we'll send you a link to reset your password.
                            </p>
                        </div>

                        <div className="flex flex-column gap-4">
                            {/* Success Message */}
                            {success && (
                                <div className="text-center">
                                    <i className="pi pi-check-circle text-green-500 text-2xl mb-2"></i>
                                    <small className="text-green-600 block">
                                        Password reset link sent to your email!
                                    </small>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && !success && (
                                <small className="p-error">{error}</small>
                            )}

                            <div className="p-input-icon-left w-full">
                                <i className="pi pi-envelope"></i>
                                <InputText
                                    id="email"
                                    type="text"
                                    className={`w-full md:w-25rem${error && !success ? ' p-invalid' : ''}`}
                                    placeholder="Email Address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    disabled={loading || success}
                                    autoComplete="email"
                                />
                            </div>

                            <div className="flex gap-2">
                                <Button 
                                    label="CANCEL"
                                    className="flex-1 p-button-outlined"
                                    onClick={handleCancel}
                                    disabled={loading || success}
                                />
                                <Button 
                                    label={loading ? 'SENDING...' : (success ? 'SENT!' : 'SEND RESET LINK')}
                                    className="flex-1"
                                    onClick={handleSubmit}
                                    loading={loading}
                                    disabled={loading || success}
                                />
                            </div>
                            
                            <span className="font-semibold text-color-secondary">
                                Remember your password? <Link to={buildAuthUrl('/app/login')} className="font-semibold cursor-pointer primary-color">Back to Login</Link>
                            </span>
                        </div>
                    </div>

                </div>

                {/* Right Panel - Hero Section */}
                <div className="w-8 hidden lg:flex flex-column justify-content-center align-items-center px-6 py-6 auth-hero-section">
                    <div className="text-center">
                        <span className="block text-white text-7xl font-bold mb-4">
                            INTERESTNG
                        </span>
                        <span className="block text-white text-5xl font-semibold mb-4">
                            Account
                        </span>
                        <span className="block text-white text-xl line-height-3">
                            Comprehensive credit reports and analysis
                            <br />at your fingertips. Secure, fast, and reliable
                            <br />financial data management.
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ForgotPassword;